/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { SpecTemplateStatus } from '@gh/builder-shared-data';
import { createDefaultGridModel, ROW_DIRTY_CLASS, ROW_DISABLED_CLASS } from '@gh/common-internal-shared';
import { queryFormMode, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { isEditFormMode } from '@gh/core-ui';
import {
  buildEditableSelectColumn,
  CellParams,
  EditableCellOptionSet,
  GridModel,
  LayoutBasedGridDefinitionWithModel,
} from '@gh/core-ui-grid';
import { isNotEmpty } from '@gh/core-util';
import { ColDef, GridOptions } from 'ag-grid-community';
import { constant, flow, isEmpty, isNil } from 'lodash';
import {
  getPbLinkRecordId,
  PbLinkJst,
  PbLinkJstParagraph,
  PbLinkJstRecord,
  pbLinkJstRecordFieldState,
  PbLinkJstSection,
  validatePbLinkJstRecordField,
} from '../../resources/price-book-link.model';
import { PriceBookLinkCommandService } from './price-book-link-command.service';
import {
  PriceBookLinkJstState,
  PriceBookLinkState,
  queryPbLinkJst,
  queryPbLinkJstParagraph,
  queryPbLinkJstSection,
} from './price-book-link.state';

@Injectable()
export class PriceBookLinkJstGridDefinition extends LayoutBasedGridDefinitionWithModel {

  constructor(private commands: PriceBookLinkCommandService,
              private state: State<PriceBookLinkState>,
              private translations: TranslationService) {
    super();
  }

  get editable(): boolean {
    return isEditFormMode(this.state.project(queryFormMode));
  }

  buildGridModel(): GridModel {
    return createDefaultGridModel({
      editable: this.editable,
      getId: getPbLinkRecordId,
      getCellDef: pbLinkJstRecordFieldState,
      validate: validatePbLinkJstRecordField,
      command: (entity, name, value) => this.commands.setRecordField(getPbLinkRecordId(entity), name, value),
      getValue: (field, state) => {
        switch (field) {
          case 'paragraph':
            return state.value.paragraphId;
          case 'section':
            return state.value.sectionId;
          default:
            return state.value;
        }
      },
    });
  }

  buildGridOptions(): GridOptions {
    return {
      singleClickEdit: true,
      getRowNodeId: getPbLinkRecordId,
      rowClassRules: {
        [ROW_DIRTY_CLASS]: ({ data }: { data: PbLinkJstRecord }) => !isNil(data.dirtyType),
        [ROW_DISABLED_CLASS]: ({ data }: { data: PbLinkJstRecord }) =>
          this.getTemplate(getPbLinkRecordId(data)).status === SpecTemplateStatus.Deleted,
      },
    };
  }

  buildColumnDefs(): Hash<ColDef> {
    return {
      paragraph: buildEditableSelectColumn(
        this.buildParagraphOptionSet(),
        this.model.getCellAccessor('paragraph'),
        {
          hasAlternativeText: ({ data: { recordId } }: CellParams) => isEmpty(this.getTemplate(recordId).paragraphs),
          getAlternativeText: constant(this.translations.get('lbl_no_paragraphs')),
          forceClearable: true,
        }),
      section: buildEditableSelectColumn(
        this.buildSectionOptionSet(),
        this.model.getCellAccessor('section'),
        {
          hasAlternativeText: ({ data: { section: { paragraphId } } }: CellParams) =>
            paragraphId && isEmpty(this.getParagraph(paragraphId).sections),
          getAlternativeText: constant(this.translations.get('lbl_no_sections')),
          useEditableAsEnabled: this.editable,
          forceClearable: true,
        }),
    };
  }

  private buildParagraphOptionSet(): EditableCellOptionSet {
    const getOptions = ({ data: { recordId } }: CellParams) => this.getTemplate(recordId).paragraphs;
    return {
      getOptions,
      hasOptions: flow(getOptions, isNotEmpty),
      format: ({ data: { paragraph: { paragraphId } } }) => paragraphId && this.getParagraph(paragraphId).name,
    };
  }

  private buildSectionOptionSet(): EditableCellOptionSet {
    const getOptions = ({ data: { section: { paragraphId } } }: CellParams) => this.getParagraph(paragraphId).sections;
    return {
      getOptions,
      hasOptions: flow(getOptions, isNotEmpty),
      format: ({ data: { section: { sectionId } } }) => sectionId && this.getSection(sectionId).name,
    };
  }

  private getTemplate(id: ID): PbLinkJst {
    // tslint:disable-next-line:no-non-null-assertion
    return this.state.project((state: PriceBookLinkJstState) => queryPbLinkJst(state, id))!;
  }

  private getParagraph(paragraphId: ID): PbLinkJstParagraph {
    return this.state.project((state: PriceBookLinkJstState) => queryPbLinkJstParagraph(state, paragraphId));
  }

  private getSection(sectionId: ID): PbLinkJstSection {
    return this.state.project((state: PriceBookLinkJstState) => queryPbLinkJstSection(state, sectionId));
  }
}
